<template>
    <div class="quadrant">
        <div
            v-if="quadrant.title"
            class="quadrant__title"
            :id="`${onDescriptionNullChangeClass}`"
            v-b-popover.hover.bottom="`${quadrant.description}`"
            :disabled="!quadrant.description"
        >
            {{ quadrant.title }}
        </div>
        <DragAble
            :list="users"
            :group="`n-box-${nBox.id}`"
            class="quadrant__users scroll"
            :disabled="!canDragAndDropUsers"
            @add="onChangeUserQuadrant($event, quadrant)"
            @update="onChangeUserQuadrant($event, quadrant)"
        >
            <transition-group
                tag="div"
                name="list"
                type="transition"
                class="h-100 w-100"
            >
                <div
                    class="mb-2"
                    :user-id="user.id"
                    :id="`${user.id}-${quadrant.id}`"
                    :key="user.id"
                    v-for="user in users"
                    @click="onClickUser(user)"
                >
                    <UserCard
                        :user="user"
                        :nBoxId="nBox.id"
                        :visualizationType="visualizationType"
                    />
                </div>
            </transition-group>
        </DragAble>
        <div class="quadrant__info">
            <span class="mr-2">
                <i class="fas fa-user" />
                {{ quadrant.info.users_count }}
            </span>
            <span>
                {{ `(${quadrant.info.users_percent}%)` }}
            </span>
        </div>
    </div>
</template>

<script>
import i18n from '@/i18n'
import Avatar from 'vue-avatar'
import { EventBuzz } from '@/main'
import DragAble from 'vuedraggable'
import ActualValue from '../actual-value'
import { validationMixin } from 'vuelidate'
import { calibrateUsers } from '../calibration/api'
import { getCentralValue } from '@/views/n-box/utils'
import { UserCard } from './components'
import notification, { success, danger } from '@/lib/notification'

notification.init()

export default {
    components: {
        Avatar,
        UserCard,
        DragAble,
        ActualValue,
    },
    mixins: [validationMixin],
    props: {
        nBox: {
            type: Object,
            required: true,
        },
        quadrant: {
            type: Object,
            require: true,
        },
        visualizationType: {
            type: String,
            require: true,
        },
    },
    mounted() {
        this.setUsers()
    },
    data() {
        return {
            users: [],
        }
    },
    watch: {
        'quadrant.users': function() {
            this.setUsers()
        },
    },
    computed: {
        canCalibrate() {
            return this.visualizationType != 'pre-calibrated'
        },
        canDragAndDropUsers() {
            return this.nBox.calibration_type == 'quadrant' && this.canCalibrate
        },
        onDescriptionNullChangeClass() {
            if (this.quadrant.description) {
                return 'descriptionEmpty'
            }
            return 'descriptionFilled'
        },
    },
    methods: {
        setUsers() {
            this.users = [...this.quadrant.users]
        },
        onChangeUserQuadrant(event, quadrant) {
            if (!this.canCalibrate) {
                return
            }

            const newIndex = event.newIndex
            const userId = event.item.getAttribute('user-id')
            this.calibrateUser(userId, newIndex, quadrant)
        },
        calibrateUser(userId, newIndex, quadrant) {
            const { x, y } = this.getScores(newIndex, quadrant)
            const data = {
                x_score: x,
                y_score: y,
                user_id: userId,
                n_box_id: this.nBox.id,
            }

            calibrateUsers(this.nBox.id, [data])
                .then(() => {
                    success(i18n.t('notification.usersCalibrationSuccess'))
                    EventBuzz.$emit(
                        `users-calibrated-n-box-${this.nBox.id}`,
                        this.nBox.id
                    )
                })
                .catch(danger)
        },
        getScores(newIndex, quadrant) {
            if (newIndex == 0) {
                return this.calculateFirstPositionScores(quadrant)
            } else if (this.users.length - 1 == newIndex) {
                return this.calculateLastPositionScores(quadrant)
            } else {
                return this.calculateMiddlePositionScores(newIndex)
            }
        },
        calculateFirstPositionScores(quadrant) {
            const oldTopUser = this.getUserByIndex(1)
            if (oldTopUser) {
                return this.createScoresObject(
                    oldTopUser.x,
                    quadrant.x_max,
                    oldTopUser.y,
                    quadrant.y_max
                )
            }

            return this.createScoresObject(
                quadrant.x_min,
                quadrant.x_max,
                quadrant.y_min,
                quadrant.y_max
            )
        },
        calculateLastPositionScores(quadrant) {
            const oldLastUser = this.getUserByIndex(this.users.length - 2)

            return this.createScoresObject(
                quadrant.x_min,
                oldLastUser.x,
                quadrant.y_min,
                oldLastUser.y
            )
        },
        calculateMiddlePositionScores(newIndex) {
            const userAbove = this.getUserByIndex(newIndex + 1)
            const userBellow = this.getUserByIndex(newIndex - 1)
            return this.createScoresObject(
                userBellow.x,
                userAbove.x,
                userBellow.y,
                userAbove.y
            )
        },
        createScoresObject(xMin, xMax, yMin, yMax) {
            return {
                x: getCentralValue(xMin, xMax),
                y: getCentralValue(yMin, yMax),
            }
        },
        getUserById(id) {
            return this.users.find(u => u.id == id)
        },
        getUserByIndex(index) {
            return this.users[index]
        },
        onClickUser(user) {
            this.$emit('user-clicked', user)
        },
    },
}
</script>

<style scoped lang="scss">
.quadrant {
    color: gray;
    display: flex;
    flex-direction: column;
    position: relative;
    width: inherit;
    height: inherit;
    background-color: rgba(0, 155, 219, 0.05);
}

.quadrant__title {
    text-align: center;
    margin: 0.5rem;
    margin-bottom: 0;
    font-weight: 600;
    justify-content: center;
}

#descriptionEmpty {
    cursor: help;
}

#descriptionFilled {
    cursor: auto;
}

.quadrant__users {
    height: inherit;
    padding: 10px;
    padding-top: 0;
    margin-top: 7px;
    margin-right: 5px;
}

.quadrant__info {
    display: flex;
    padding: 5px;
    align-items: center;
    border-radius: 5px;
    margin: 0.5rem;
    text-align: end;
    font-size: 12px;
    align-self: flex-end;
    background-color: white;
    box-shadow: 0 1px 2px 1px rgb(206, 211, 215);
}

.list-move {
    transition: transform 0.5s;
}

.list-leave {
    display: none;
}

.list-leave-to {
    display: none;
}

.no-move {
    transition: transform 0s;
}
</style>
